import React from 'react';
import moment from 'moment';
import EntityContractPhaseManager from './EntityContractPhaseManager';
import JobResourceMultiSelect from '../job/JobResourceMultiSelect';
import AdditionalEquipmentJobResourceManager from './AdditionalEquipmentJobResourceManager';
import { Weekdays } from '../Common';
import { getEventDisplayDate } from './LongTermSchedule';
import { FormGroup, Input } from 'reactstrap';
import {
    FormLabel,
    ValidationErrorMessage,
} from '../common/forms/FormElements';
import Select from 'react-select';
import ResourceApplicationUserGroupMultiSelect from '../job/ResourceApplicationUserGroupMultiSelect';
import AdditionalLaborJobResourceManager from './AdditionalLaborJobResourceManager';
import JobResourceOverrideSpecifics from '../job/JobResourceOverrideSpecifics';

export default class PublishedLongTermScheduleDetails extends React.Component {
    getDayName = () => {
        const { eventDate } = this.props;

        if (!eventDate) {
            return null;
        }

        return Weekdays[moment(eventDate).day()].name.toLowerCase();
    };

    getLowboyResourcesForDay = () => {
        const { job, eventDate } = this.props;

        if (!job || !job.lowboyResources) {
            return [];
        }

        return job.lowboyResources.filter((lr) =>
            moment(lr.date).isSame(eventDate, 'day')
        );
    };

    getJobResourceValue = (applicationGroupId, equipmentGroupId) => {
        const { publishedDayName } = this.props;

        const jobResource = this.getJobResource(
            applicationGroupId,
            equipmentGroupId
        );

        if (!jobResource) {
            return '';
        }

        return jobResource[publishedDayName];
    };

    getJobResource = (applicationGroupId, equipmentGroupId) => {
        const { job } = this.props;

        return job.resources.find(
            (jr) =>
                (applicationGroupId &&
                    jr.applicationGroupId == applicationGroupId) ||
                (equipmentGroupId && jr.equipmentGroupId == equipmentGroupId)
        );
    };

    getJobResourceOverrides = (applicationGroupId, equipmentGroupId) => {
        const { jobResourceOverrides } = this.props;
        const jobResource = this.getJobResource(
            applicationGroupId,
            equipmentGroupId
        );

        return (jobResourceOverrides || []).filter(
            (jobResourceOverride) =>
                jobResourceOverride.jobResourceId === jobResource?.id
        );
    };

    render() {
        const {
            longTermSchedule,
            contractPhases,
            equipmentTypes,
            cancelWorkflowEnabled,
            job,
            eventDate,
            existingJobContractPhases,
            equipmentTypeCounts,
            equipmentTypeIdsRequiringMoves,
            onResourcesUpdated,
            onJobContractPhaseChange,
            isReleased,
            jobOverride,
            isCancelled,
            equipmentGroups,
            applicationGroups,
            onChange,
            onJobResourceChange,
            arcGisLocations,
            onJobOverrideArcGisLocationChange,
            saving,
            signalChange,
        } = this.props;

        if (!longTermSchedule || !longTermSchedule.isPublished) {
            return null;
        }

        const dayName = this.getDayName();
        const lowboyResources = this.getLowboyResourcesForDay();

        const applicationGroup = applicationGroups.find(
            (ag) =>
                longTermSchedule.scheduleApplicationGroupCount
                    .applicationGroupId == ag.id
        );

        const anyLocationsDefined = Boolean(
            arcGisLocations && arcGisLocations.length > 0
        );

        const ltsArcGisLocation =
            (arcGisLocations || []).find(
                (agl) => agl.id == longTermSchedule.arcGisLocationId
            ) || {};

        const arcGisLocationPlaceholder = ltsArcGisLocation.addrLoc;

        return (
            <>
                {!cancelWorkflowEnabled && (
                    <>
                        <EntityContractPhaseManager
                            contractPhases={contractPhases}
                            entityContractPhases={job.jobContractPhases.filter(
                                (jcp) =>
                                    jcp.dayOfWeek == moment(eventDate).day()
                            )}
                            labelText="Phases"
                            onEntityContractPhaseChange={
                                onJobContractPhaseChange
                            }
                            entityId={longTermSchedule.id}
                            existingEntityContractPhases={
                                existingJobContractPhases
                            }
                        />

                        <JobResourceMultiSelect
                            equipmentTypes={equipmentTypes}
                            jobResources={job.resources}
                            dayName={dayName}
                            readOnly={true}
                            labelText={`Equipment Resources - ${getEventDisplayDate(eventDate)}`}
                            equipmentTypeCounts={equipmentTypeCounts}
                            dayOfDate={eventDate}
                            lowboyResources={lowboyResources}
                            equipmentTypeIdsRequiringMoves={
                                equipmentTypeIdsRequiringMoves
                            }
                            job={job}
                            onResourcesUpdated={onResourcesUpdated}
                            saving={saving}
                            hideInlineSave={true}
                            signalChange={signalChange}
                        />

                        <AdditionalEquipmentJobResourceManager
                            equipmentTypes={equipmentTypes}
                            job={job}
                            eventDate={eventDate}
                            onResourcesUpdated={onResourcesUpdated}
                            equipmentTypeCounts={equipmentTypeCounts}
                            dayOfDate={eventDate}
                            equipmentTypeIdsRequiringMoves={
                                equipmentTypeIdsRequiringMoves
                            }
                            lowboyResources={lowboyResources}
                            saving={saving}
                            signalChange={signalChange}
                        />

                        <ResourceApplicationUserGroupMultiSelect
                            {...this.state}
                            {...this.props}
                            jobResources={job.resources}
                            labelText={`Labor Resources - ${getEventDisplayDate(eventDate)}`}
                            dayName={dayName}
                            readOnly={true}
                        />

                        <AdditionalLaborJobResourceManager
                            {...this.state}
                            {...this.props}
                            onResourcesUpdated={onResourcesUpdated}
                            dayOfDate={eventDate}
                            signalChange={signalChange}
                        />
                    </>
                )}

                {!cancelWorkflowEnabled && !isReleased && (
                    <>
                        <div className="d-flex justify-content-between published-long-term-schedule-details">
                            {!anyLocationsDefined && (
                                <FormGroup className="w-14">
                                    <FormLabel text="Road Name" />
                                    <Input
                                        id="jobOverride.roadName"
                                        name="jobOverride.roadName"
                                        value={jobOverride.roadName ?? ''}
                                        placeholder={longTermSchedule.roadName}
                                        onChange={onChange}
                                        type="text"
                                        disabled={isReleased || isCancelled}
                                        maxLength={100}
                                    />
                                    <ValidationErrorMessage>
                                        Road Name is required.
                                    </ValidationErrorMessage>
                                </FormGroup>
                            )}

                            {anyLocationsDefined && (
                                <FormGroup className="w-25">
                                    <FormLabel text="Location" required />
                                    <Select
                                        id="jobOverride.arcGisLocationId"
                                        name="jobOverride.arcGisLocationId"
                                        options={arcGisLocations}
                                        getOptionValue={(agl) => agl.id}
                                        getOptionLabel={(agl) =>
                                            agl.isActive
                                                ? agl.addrLoc
                                                : `[INACTIVE] ${agl.addrLoc}`
                                        }
                                        value={
                                            arcGisLocations.filter(
                                                (agl) =>
                                                    agl.id ==
                                                    jobOverride.arcGisLocationId
                                            ) ?? ''
                                        }
                                        onChange={
                                            onJobOverrideArcGisLocationChange
                                        }
                                        menuPlacement="top"
                                        placeholder={arcGisLocationPlaceholder}
                                        isClearable={true}
                                    />
                                </FormGroup>
                            )}

                            <FormGroup className="w-14">
                                <FormLabel text={applicationGroup.groupName} />
                                <Input
                                    id="jobFlaggerResourceCount"
                                    name="jobFlaggerResourceCount"
                                    value={this.getJobResourceValue(
                                        applicationGroup.id
                                    )}
                                    onChange={onJobResourceChange.bind(this, {
                                        applicationGroupId: applicationGroup.id,
                                    })}
                                    type="number"
                                    disabled={isReleased || isCancelled}
                                    valid={
                                        !this.getJobResourceValue(
                                            applicationGroup.id
                                        ) ||
                                        this.getJobResourceValue(
                                            applicationGroup.id
                                        ) < 0
                                    }
                                />
                                <ValidationErrorMessage>
                                    Count should be greater than zero.
                                </ValidationErrorMessage>
                                <JobResourceOverrideSpecifics
                                    jobResourceOverrides={this.getJobResourceOverrides(
                                        applicationGroup.id
                                    )}
                                    arcGisLocations={arcGisLocations}
                                    resourceLabel={applicationGroup.groupName}
                                />
                            </FormGroup>

                            {equipmentGroups.map((eg, i) => {
                                const indexLookup = `jobResource-${i}`;
                                const resourceValue = this.getJobResourceValue(
                                    null,
                                    eg.id
                                );

                                const equipmentLabel = eg.isTruck
                                    ? `Brokered ${eg.name}`
                                    : eg.name;

                                return (
                                    <FormGroup
                                        key={`jr-eg-${eg.id}`}
                                        className="w-14"
                                    >
                                        <FormLabel text={equipmentLabel} />

                                        <Input
                                            id={indexLookup}
                                            name={indexLookup}
                                            value={resourceValue}
                                            onChange={onJobResourceChange.bind(
                                                this,
                                                {
                                                    equipmentGroupId: eg.id,
                                                }
                                            )}
                                            type="number"
                                            disabled={isReleased || isCancelled}
                                            invalid={
                                                resourceValue === '' ||
                                                resourceValue === null ||
                                                resourceValue < 0
                                            }
                                            min={0}
                                        />
                                        <ValidationErrorMessage>
                                            Count should be greater than zero.
                                        </ValidationErrorMessage>

                                        <JobResourceOverrideSpecifics
                                            jobResourceOverrides={this.getJobResourceOverrides(
                                                null,
                                                eg.id
                                            )}
                                            arcGisLocations={arcGisLocations}
                                            resourceLabel={equipmentLabel}
                                            className="mt-2"
                                        />
                                    </FormGroup>
                                );
                            })}
                        </div>
                    </>
                )}
            </>
        );
    }
}
